/**
 * SitePick front — REST 取得の共通化（docs/06 §2 / §6）
 *
 * `POST /wp-json/sitepick/v1/sites/batch` を叩き、favorites / compare の表示に必要な
 * 最新データを返す。request / abort / HTTP error / JSON error / 429 を共通で扱う。
 * 同一ページで同じ ID 群が同時に要求されたときは in-flight リクエストを共有する。
 */

import { sanitizeIds } from "./sanitize.js";

/**
 * バッチ取得のエラー。`kind` で分岐できる。
 */
export class SiteApiError extends Error {
  /**
   * @param {string} kind "network" | "http" | "parse" | "rate_limited" | "empty"
   * @param {Object} [meta]
   */
  constructor(kind, meta = {}) {
    super(meta.message || kind);
    this.name = "SiteApiError";
    this.kind = kind;
    this.status = meta.status || 0;
    this.retryAfter = meta.retryAfter || 0;
  }
}

/** @type {Map<string, Promise<Object[]>>} */
const inFlight = new Map();

/**
 * REST のベース URL（末尾スラッシュ付き）。
 *
 * @returns {string}
 */
function restBase() {
  const cfg = window.SITEPICK || {};
  const base = typeof cfg.restUrl === "string" && cfg.restUrl ? cfg.restUrl : "/wp-json/sitepick/v1/";
  return base.endsWith("/") ? base : `${base}/`;
}

/**
 * 商品バッチを取得する。
 *
 * @param {number[]} rawIds
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 * @returns {Promise<Object[]>} 順序はリクエスト順（サーバが保証）
 */
export function fetchBatchSites(rawIds, options = {}) {
  const ids = sanitizeIds(rawIds, 50);
  if (ids.length === 0) {
    return Promise.resolve([]);
  }

  const dedupeKey = ids.slice().sort((a, b) => a - b).join(",");
  if (!options.signal && inFlight.has(dedupeKey)) {
    return inFlight.get(dedupeKey);
  }

  const promise = doFetch(ids, options.signal).finally(() => {
    if (inFlight.get(dedupeKey) === promise) {
      inFlight.delete(dedupeKey);
    }
  });

  if (!options.signal) {
    inFlight.set(dedupeKey, promise);
  }
  return promise;
}

/**
 * @param {number[]} ids
 * @param {AbortSignal} [signal]
 * @returns {Promise<Object[]>}
 */
async function doFetch(ids, signal) {
  let response;
  try {
    response = await fetch(`${restBase()}sites/batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ ids }),
      credentials: "same-origin",
      signal,
    });
  } catch (err) {
    if (err && err.name === "AbortError") {
      throw err;
    }
    throw new SiteApiError("network", { message: "network error" });
  }

  if (response.status === 429) {
    const retryAfter = parseInt(response.headers.get("Retry-After") || "0", 10);
    throw new SiteApiError("rate_limited", {
      status: 429,
      retryAfter: Number.isFinite(retryAfter) ? retryAfter : 0,
    });
  }

  if (!response.ok) {
    throw new SiteApiError("http", { status: response.status, message: `HTTP ${response.status}` });
  }

  let data;
  try {
    data = await response.json();
  } catch {
    throw new SiteApiError("parse", { message: "invalid JSON" });
  }

  return Array.isArray(data) ? data : [];
}
